import { DecimalPipe } from "@angular/common";
import { Component, ChangeDetectionStrategy, OnInit, OnDestroy, Output, EventEmitter, Input, ChangeDetectorRef } from "@angular/core";
import { AppService } from "@irb/app.service";
import { CalltoactionService } from "@irb/irb-common/components/calltoaction/calltoaction.service";
import { IrbClientsEvents } from "@irb/irb-common/events";
import { IDropdownItem } from "@irb-common/models/dropdown.model";
import { ICoveredCall, ICoveredCallAllocation } from "@irb/irb-editors/rebalance/irb-rebalance.model";
import { RebalanceService } from "@irb/irb-editors/rebalance/rebalance.service";
import { OptionsFormatterService } from "../../options-formatter.service";
import { PurchaseUnderlyingGridLayoutService } from "./purchase-underlying.grid-layout";
import { IrbOptionChainsService } from "@irb/irb-option-chains/irb-option-chains.service";
import { Subscription } from "rxjs";

@Component({ standalone: false,
    selector: "purchase-underlying-settings",
    templateUrl: "./purchase-underlying-settings.component.html",
    styleUrls: [ 
        "./purchase-underlying-settings.component.scss"
    ],
    changeDetection: ChangeDetectionStrategy.Eager
})
export class PurchaseUnderlyingSettingsComponent implements OnInit, OnDestroy {

    @Output() change = new EventEmitter<any>();
    
    _data: ICoveredCall;
    _isReadOnly: boolean;
    _isCashTarget: boolean = true;
    _defaultPercentOfUnderlyingToCover: number;
    _defaultPercentOfUnderlyingToPurchase: number

    _grid: any;
    _gridOptions: any;

    percentOfUnderlyingDropdown: Array<IDropdownItem> = [
        { name: 'Model %', value: 'MODEL_PERC' },
        { name: 'Specify %', value: 'SPECIFY_PERC' },
    ];

    private optionChainSubscription: Subscription;
    isUitest = false;
    frameworkComponents: any;

    constructor(private cdr: ChangeDetectorRef,
        private gls: PurchaseUnderlyingGridLayoutService,
        private ctas: CalltoactionService,
        private as: AppService,
        private readonly rebalanceService: RebalanceService,
        public decimalPipe: DecimalPipe,
        public optionFormatter: OptionsFormatterService,
        private irbOptionChainsService: IrbOptionChainsService) {

        this.defaultPercentOfUnderlyingToCover = 100;
        this.defaultPercentOfUnderlyingToPurchase = 100;

        this.gridOptions = {
            columnDefs: this.gls.getColumnDefs(this),            
            floatingFilter: false,
            context: { componentParent: this },
            isExternalFilterPresent: this.isExternalFilterPresent.bind(this),
            doesExternalFilterPass: this.doesExternalFilterPass.bind(this)
        };

        this.frameworkComponents = this.gls.getFrameworkComponents();
        this.optionChainSubscription = this.irbOptionChainsService.optionChainSymbolSelections$.subscribe((optionData) => {
            this.optionChainSelected(optionData);
        });
    }

    optionChainSelected(optionChainData: Array<string>) {
        if(optionChainData && optionChainData.length === 0) {
            this.data.allocations = [];
        } else {
            this.data.allocations = [];
            optionChainData.forEach(option => {
                this.data.allocations.push({
                    underlyingSymbol: option[0], underlyingTarget: "MODEL_PERC", optionSymbol: option[1][0],
                    optionSymbolToRollTo: null,
                    percentOfUnderlyingToCover: this.defaultPercentOfUnderlyingToCover, percentOfUnderlyingToPurchase: null,
                    isUIdeleted: false, percentOfUnderlyingToTrim: 100, percentOfOptionToBuyBack: 100,
                    percentOfCoverCallToRoll: 100, allocationId: null, optionSecId: null,
                    optionSelectionMode: null, daysToExpiration: null, positionType: "LONG"
                } as ICoveredCallAllocation);
            });
        }

        this.loadGrid();
        this.change.emit(true);
    }

    onGridReady(params: any) {
        this.grid = params;
        this.grid.api.onFilterChanged();
        this.load();
    }

    loadGrid() {
        this.grid.api.setGridOption('rowData', this.data.allocations);
        // setTimeout(() => {
        //     if (this.grid && this.grid.api) {
        //         this.grid.api.sizeColumnsToFit();
        //     }
        // });
    }

    ngOnInit(): void {
    }

    ngOnDestroy(): void {
        this.optionChainSubscription.unsubscribe();
    }

    load() {
        if (!this.isReadOnly) {
            this.data.allocations = [];
        } else {
            this.populateIsCashTarget();
        }
        this.loadGrid();
    }

    getDefaultValueForppPercentOfUnderlying(underlyingTarget: string) {
        if (underlyingTarget === undefined || underlyingTarget === null || underlyingTarget === '') {
            return { value: "MODEL_PERC", name: "Model %" };
        } else {
            return this.percentOfUnderlyingDropdown.find((x: IDropdownItem) => x.value === underlyingTarget);
        }
        
    }

    ispercentOfUnderlyingToPurchaseEditable(params: any) {
        let isEditable = true;
        if (params.data.underlyingTarget === "MODEL_PERC") {
          isEditable = false;
        } else {
          isEditable = true;
        }
    
        return isEditable;
    }

    populateIsCashTarget() {
        if (this.data.buyUsingCashTarget === true && this.data.buyUsingCashMinimum === false) {
            this.isCashTarget = true;
        } else {
            this.isCashTarget = false;
        }
    }

    onCashMinTargetChange() {
        if (this.isCashTarget) {
            this.data.buyUsingCashTarget = true;
            this.data.buyUsingCashMinimum = false;
        } else {
            this.data.buyUsingCashTarget = false;
            this.data.buyUsingCashMinimum = true;
        }
        this.change.emit(true);
    }

    applyPercentOfUnderlyingToCover() {
        if (this.data.allocations && this.data.allocations.length > 0) {
            this.data.allocations.filter(allocation => allocation.isUIdeleted === false).forEach(allocation => {
                allocation.percentOfUnderlyingToCover = Number(this.defaultPercentOfUnderlyingToCover);
            });
            this.loadGrid();
            this.change.emit(true);
        }
    }

    applyPercentOfUnderlyingToPurchase() {
        if (this.data.allocations && this.data.allocations.length > 0) {
            this.data.allocations.filter(allocation => (allocation.isUIdeleted === false && allocation.underlyingTarget === 'SPECIFY_PERC')).forEach(allocation => {
                allocation.percentOfUnderlyingToPurchase = Number(this.defaultPercentOfUnderlyingToPurchase);
            });
            this.loadGrid();
            this.change.emit(true);
        }
    }

    addAllocations() {
        this.as.fireEvent(IrbClientsEvents.SHOW_OPTION_CHAINS_DIALOG, {
            data: {action: 'PURCHASE_UNDERLYING_SELL_COVER_CALL', selectedOptions: this.getSelectedOptions()}
        });
    }

    getSelectedOptions() {
        let selectedOptions: { symbol: string; optionSymbol: string; }[] = [];
        this.data.allocations.filter(allocation => allocation.isUIdeleted === false).forEach((allocation: ICoveredCallAllocation) => {
            selectedOptions.push({symbol: allocation.underlyingSymbol, optionSymbol: allocation.optionSymbol});
        });
        return selectedOptions;
    }

    getRowCount() {
        return (this.data.allocations) ? this.data.allocations.length : 0;
    }

    deleteAll() {
        this.data.allocations = [];
        this.loadGrid();
        this.change.emit(true);
    }

    delete(allocation: any) {
        let allocations: ICoveredCallAllocation[] = [];
        for (let i = 0; i < this.data.allocations.length; i++) {
            if (this.data.allocations[i].underlyingSymbol !== allocation.underlyingSymbol) {
                allocations.push(this.data.allocations[i]);
            }
        }
        this.data.allocations = allocations;
        this.loadGrid();
        this.change.emit(true);
    }

    cancelDelete() {
        this.ctas.remove();
    }

    isExternalFilterPresent() {
        return true;
    }

    doesExternalFilterPass(node: any) {
        return !node.data.isUIdeleted;
    }

    @Input()
    get data() {
        return this._data;
    }

    set data(data: ICoveredCall) {
        this._data = data;
    }

    @Input()
    get isReadOnly() {
        return this._isReadOnly;
    }

    set isReadOnly(isReadOnly: boolean) {
        this._isReadOnly = isReadOnly;
    }

    get isCashTarget() {
        return this._isCashTarget;
    }

    set isCashTarget(isCashTarget: boolean) {
        this._isCashTarget = isCashTarget;
    }

    get defaultPercentOfUnderlyingToCover() {
        return this._defaultPercentOfUnderlyingToCover;
    }

    set defaultPercentOfUnderlyingToCover(defaultPercentOfUnderlyingToCover: number) {
        this._defaultPercentOfUnderlyingToCover = defaultPercentOfUnderlyingToCover;
    }

    get defaultPercentOfUnderlyingToPurchase() {
        return this._defaultPercentOfUnderlyingToPurchase;
    }

    set defaultPercentOfUnderlyingToPurchase(defaultPercentOfUnderlyingToPurchase: number) {
        this._defaultPercentOfUnderlyingToPurchase = defaultPercentOfUnderlyingToPurchase;
    }

    set grid(g: any) {
        this._grid = g;
    }

    get grid() {
        return this._grid;
    }

    set gridOptions(gridOpns: any) {
        this._gridOptions = gridOpns;
    }

    get gridOptions() {
        return this._gridOptions;
    }

}
